/**
 * Client-side instrumentation hook.
 *
 * Installs lightweight global error listeners so browser-side crashes and
 * unhandled promise rejections are still reported to Sentry.
 */

import { captureError, isSentryEnabled } from "@/lib/sentry";

type GlobalErrorState = typeof globalThis & {
  __MAPPITO_SENTRY_LISTENERS__?: boolean;
};

function installGlobalErrorListeners() {
  if (typeof window === "undefined" || !isSentryEnabled()) return;

  const root = globalThis as GlobalErrorState;
  if (root.__MAPPITO_SENTRY_LISTENERS__) return;
  root.__MAPPITO_SENTRY_LISTENERS__ = true;

  window.addEventListener("error", (event) => {
    const error =
      event.error ??
      new Error(
        `${event.message} (${event.filename}:${event.lineno}:${event.colno})`,
      );

    captureError(error, {
      source: "window.error",
      tags: { surface: "browser" },
      browser: {
        url: window.location.href,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(
            `Unhandled rejection: ${
              typeof reason === "string" ? reason : JSON.stringify(reason)
            }`,
          );

    captureError(error, {
      source: "window.unhandledrejection",
      tags: { surface: "browser" },
      browser: {
        url: window.location.href,
      },
    });
  });
}

installGlobalErrorListeners();

export function onRouterTransitionStart() {}
